import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import TaskIcon from '@mui/icons-material/Task'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation } from '@tanstack/react-query'
import i18n from 'i18next'
import { type ReactElement } from 'react'
import { getFile } from 'screens/TripDetails/TripDetails.service'
import type { StopDocument } from 'screens/TripDetails/TripDetails.types'

export interface DocumentLinkProps {
  file: StopDocument
  index?: number
  weblinkToken?: string
}

const getName = (file: StopDocument): string => {
  switch (file.fileType) {
    case 'POP':
      return i18n.t('attachments.types.POP')
    case 'POD':
      return i18n.t('attachments.types.POD')
    case 'TRO':
      return i18n.t('attachments.types.TRO')
    default:
      return file.name
  }
}

const DocumentLink = ({ file, index, weblinkToken }: DocumentLinkProps): ReactElement => {
  const isProof = ['POP', 'POD'].includes(file.fileType)
  const downloadFileMutation = useMutation({
    mutationFn: () => getFile(file.id, weblinkToken),
  })
  const handleClick = async (): Promise<void> => {
    const blob = await downloadFileMutation.mutateAsync()
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = file.name
    anchor.click()
    URL.revokeObjectURL(url)
    anchor.remove()
  }

  return (
    <Link
      component="button"
      type="button"
      onClick={handleClick}
      underline="always"
      sx={{
        fontSize: 'inherit',
        marginRight: 1,
      }}
    >
      <Stack spacing={0.5} direction="row" alignItems="center">
        {isProof ? <TaskIcon fontSize="small" /> : <InsertDriveFileIcon fontSize="small" />}
        <Typography
          variant="p3"
          noWrap
          sx={{
            maxWidth: isProof ? 'auto' : '8rem',
            fontSize: 'inherit',
          }}
        >
          {getName(file)}
          {index !== undefined && ` (${index + 1})`}
        </Typography>
      </Stack>
    </Link>
  )
}

export default DocumentLink
